import { Directive, Input, ElementRef, Renderer2, HostListener, ComponentRef, Injector, ApplicationRef, ComponentFactoryResolver, EmbeddedViewRef } from '@angular/core';
import { HtmlTooltipComponent } from './html-tooltip.component';

@Directive({
  selector: '[appHtmlTooltip]',
  standalone: true,
})
export class HtmlTooltipDirective {
  @Input('appHtmlTooltip') htmlContent: string = '';
  private componentRef: ComponentRef<HtmlTooltipComponent> | null = null;
  private hideTimeout: any = null;

  constructor(
    private elementRef: ElementRef,
    private appRef: ApplicationRef,
    private injector: Injector,
    private resolver: ComponentFactoryResolver
  ) {}

  @HostListener('mouseenter') onMouseEnter(): void {
    // Clear any pending hide timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    if (this.componentRef || !this.htmlContent) {
      return;
    }

    const componentFactory = this.resolver.resolveComponentFactory(HtmlTooltipComponent);
    this.componentRef = componentFactory.create(this.injector);
    this.appRef.attachView(this.componentRef.hostView);

    const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    this.setTooltipComponentProperties();
    
    // Add mouse enter/leave handlers to the tooltip itself
    domElem.addEventListener('mouseenter', () => this.onTooltipMouseEnter());
    domElem.addEventListener('mouseleave', () => this.onTooltipMouseLeave());
  }

  private setTooltipComponentProperties(): void {
    if (!this.componentRef) {
      return;
    }

    this.componentRef.instance.htmlContent = this.htmlContent;
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const tooltipWidth = this.htmlContent.length > 800 ? 500 : 250;
    const viewportWidth = window.innerWidth;
    const iconCenter = rect.left + (rect.right - rect.left) / 2;
    const spaceOnRight = viewportWidth - iconCenter;
    const spaceOnLeft = rect.left;
    
    // Calculate horizontal position
    let leftPosition: number;
    
    if (spaceOnRight < tooltipWidth && spaceOnLeft > tooltipWidth) {
      // Not enough space on right, but enough on left - align to left of icon
      leftPosition = rect.right - tooltipWidth;
    } else if (spaceOnRight < tooltipWidth / 2) {
      // Very close to right edge - align tooltip's right edge with icon's right edge
      leftPosition = rect.right - tooltipWidth;
    } else {
      // Default: align tooltip start to the center of the icon
      leftPosition = iconCenter;
    }
    
    // Ensure tooltip doesn't go off-screen on the left
    leftPosition = Math.max(8, leftPosition);
    // Ensure tooltip doesn't go off-screen on the right
    leftPosition = Math.min(viewportWidth - tooltipWidth - 8, leftPosition);
    
    this.componentRef.instance.left = leftPosition;
    this.componentRef.instance.top = rect.bottom;
  }

  @HostListener('mouseleave') onMouseLeave(): void {
    // Delay hiding the tooltip to allow user to move mouse to it
    this.hideTimeout = setTimeout(() => {
      this.destroy();
    }, 300); // 0.3  second delay before hiding
  }

  private onTooltipMouseEnter(): void {
    // Cancel hide if user moves mouse into tooltip
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private onTooltipMouseLeave(): void {
    // Delay hiding the tooltip when leaving it
    this.hideTimeout = setTimeout(() => {
      this.destroy();
    }, 200); // 0.2 second delay before hiding
  }

  ngOnDestroy(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    this.destroy();
  }

  destroy(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    if (this.componentRef) {
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }
}
